/**
 * Tier quantity sync (Easify Product Options <-> theme quantity input)
 *
 * Keeps the product quantity in sync with a "tier" button option rendered by
 * the Easify Product Options app, e.g. "10 OR MORE" / "30 OR MORE" / "50 OR MORE"
 * or "1-2" / "3-4" / "5+".
 *
 * - Selecting a tier sets the quantity to that tier's minimum.
 * - Changing the quantity selects the tier that matches it.
 *
 * The app still owns the pricing: this script only clicks the app's own tier
 * buttons, so the app recalculates its add-on price as usual.
 */
(() => {
  const TIER_LABEL = /^\s*(\d+)\s*(?:(\+|or more|and up|and more)|[-–]\s*(\d+))\s*$/i;
  const OPTION_SELECTOR = '.easify-product-options .po-option-root';
  const ITEM_SELECTOR = '.po-optionItem-item';
  const LABEL_SELECTOR = '.po-buttonOption-label';
  const SELECTED_SELECTOR = '.po-buttonOption-selected';

  let syncing = false;

  const parseTier = (text) => {
    const match = (text || '').match(TIER_LABEL);
    if (!match) return null;
    return { min: parseInt(match[1], 10), max: match[3] ? parseInt(match[3], 10) : Infinity };
  };

  // Returns the visible tier option of a product form with its tiers sorted by minimum.
  const getTierOption = (root) => {
    const options = root.querySelectorAll(OPTION_SELECTOR);
    for (const option of options) {
      if (!option.getClientRects().length) continue;
      const items = Array.from(option.querySelectorAll(ITEM_SELECTOR));
      if (items.length < 2) continue;

      const tiers = items.map((item) => {
        const tier = parseTier(item.querySelector(LABEL_SELECTOR)?.textContent);
        return tier && { ...tier, item, key: `${option.dataset.optionId}:${tier.min}` };
      });
      if (tiers.some((tier) => !tier)) continue;

      tiers.sort((a, b) => a.min - b.min);
      tiers.forEach((tier, index) => {
        const next = tiers[index + 1];
        if (tier.max === Infinity && next) tier.max = next.min - 1;
      });

      const formId = option.querySelector('input[form]')?.getAttribute('form');
      return { option, tiers, formId };
    }
    return null;
  };

  const getQuantityInput = (formId) => {
    if (!formId) return null;
    return (
      document.querySelector(`input[name="quantity"][form="${formId}"]`) ||
      document.getElementById(formId)?.querySelector('input[name="quantity"]')
    );
  };

  const getSelectedTier = (tiers) => tiers.find((tier) => tier.item.querySelector(SELECTED_SELECTOR));

  const getTierForQuantity = (tiers, quantity) =>
    tiers.find((tier) => quantity >= tier.min && quantity <= tier.max) ||
    (quantity < tiers[0].min ? tiers[0] : tiers[tiers.length - 1]);

  const setQuantity = (input, quantity) => {
    if (parseInt(input.value, 10) === quantity) return;
    input.value = quantity;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  // Easify tier buttons toggle: clicking the selected tier unselects it. The app re-renders
  // asynchronously, so remember the last requested tier to never click the same one twice.
  let lastRequest = { key: null, time: 0 };

  const selectTier = (tier) => {
    if (lastRequest.key === tier.key && Date.now() - lastRequest.time < 1000) return;
    lastRequest = { key: tier.key, time: Date.now() };
    syncing = true;
    tier.item.click();
    syncing = false;
  };

  // Quantity changed -> select the matching tier (and lift the quantity to the lowest tier if needed).
  const syncTierToQuantity = (input) => {
    const tierOption = getTierOption(document);
    if (!tierOption || getQuantityInput(tierOption.formId) !== input) return;

    const { tiers } = tierOption;
    let quantity = parseInt(input.value, 10);
    if (isNaN(quantity) || quantity < tiers[0].min) {
      quantity = tiers[0].min;
      syncing = true;
      setQuantity(input, quantity);
      syncing = false;
    }

    const target = getTierForQuantity(tiers, quantity);
    if (getSelectedTier(tiers) !== target) selectTier(target);
  };

  // Tier clicked by the customer -> set the quantity to the tier minimum.
  document.addEventListener('click', (event) => {
    if (syncing) return;
    const item = event.target.closest(ITEM_SELECTOR);
    if (!item) return;

    const tierOption = getTierOption(document);
    const tier = tierOption?.tiers.find((entry) => entry.item === item);
    if (!tier) return;

    const input = getQuantityInput(tierOption.formId);
    if (!input) return;
    syncing = true;
    setQuantity(input, tier.min);
    syncing = false;
  });

  // Quantity typed or changed with the +/- buttons.
  document.addEventListener('change', (event) => {
    if (syncing || !event.target.matches('input[name="quantity"]')) return;
    syncTierToQuantity(event.target);
  });

  const init = () => {
    const tierOption = getTierOption(document);
    if (!tierOption) return;
    const input = getQuantityInput(tierOption.formId);
    if (input) syncTierToQuantity(input);
  };

  // The theme resets the quantity without a change event (e.g. after adding to cart).
  const pubsub = window.theme?.pubsub;
  if (pubsub?.PUB_SUB_EVENTS?.quantityUpdate) {
    pubsub.subscribe(pubsub.PUB_SUB_EVENTS.quantityUpdate, () => {
      if (!syncing) setTimeout(init);
    });
  }

  // Easify renders its options asynchronously and swaps the tier option when another option
  // changes (e.g. Light-Up Logo shows its own tiers): re-sync whenever the tier set changes.
  let currentTierSet = null;
  let pending = false;

  const onRender = () => {
    pending = false;
    const tierOption = getTierOption(document);
    const tierSet = tierOption
      ? `${tierOption.option.dataset.optionId}:${tierOption.tiers.map((tier) => tier.min).join(',')}`
      : null;
    if (tierSet === currentTierSet) return;
    currentTierSet = tierSet;
    if (tierSet) init();
  };

  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(onRender);
  }).observe(document.body, { childList: true, subtree: true });

  onRender();
})();
